import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { motion, useInView, useAnimation } from "framer-motion";
import {
  Zap,
  MousePointerClick,
  Gift,
  CreditCard,
  Globe,
  Star,
  Shield,
  MessageCircle,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  ChevronRight,
  Play,
  Package,
  Layers,
  Settings,
  Eye,
} from "lucide-react";

// アニメーション用のカスタムフック
const useScrollAnimation = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return { ref, controls, isInView };
};

// カウントアップアニメーション
const CountUp: React.FC<{ end: number; suffix?: string; duration?: number }> = ({
  end,
  suffix = "",
  duration = 2
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// JOYBASEニュースデータ（静的）
const joybaseNews = [
  {
    id: 4,
    title: "デザイン設定機能をリリースしました",
    date: "2026/01/15",
    category: "機能追加",
    slug: "design-settings-release"
  },
  {
    id: 3,
    title: "友だち招待機能をリリースしました",
    date: "2026/01/12",
    category: "機能追加",
    slug: "friend-referral-release"
  },
  {
    id: 1,
    title: "JOYBASEベータ版がリリースされました",
    date: "2025/10/31",
    category: "リリース",
    slug: "joybase-beta-release"
  }
];

const IndexPage: React.FC = () => {

  const features = [
    {
      title: "初期費用0円",
      description: "初期費用・月額費用0円でサイト開設を申し込めます。",
      icon: <Zap className="w-10 h-10" />,
      color: "bg-yellow-400",
    },
    {
      title: "簡単操作",
      description: "初心者でも直感的に操作可能。専門知識は不要です。",
      icon: <MousePointerClick className="w-10 h-10" />,
      color: "bg-blue-400",
    },
    {
      title: "1日1回限定ガチャ",
      description: "限定感を演出して集客力UP。リピーター獲得に効果的。",
      icon: <Gift className="w-10 h-10" />,
      color: "bg-pink-400",
    },
    {
      title: "10連ガチャ",
      description: "10連ガチャ機能搭載で売上アップをサポート。",
      icon: <Sparkles className="w-10 h-10" />,
      color: "bg-purple-400",
    },
    {
      title: "ラストワン・キリ番賞",
      description: "特別な演出で購買意欲を刺激します。",
      icon: <Star className="w-10 h-10" />,
      color: "bg-orange-400",
    },
    {
      title: "カード・ウォレット決済",
      description: "クレジットカード・Apple Pay・Google Payに対応。PayPayも対応準備中です。",
      icon: <CreditCard className="w-10 h-10" />,
      color: "bg-green-400",
    },
    {
      title: "複数オリパサイト（対応予定）",
      description: "1アカウントで複数サイトを管理できる機能を開発予定です。",
      icon: <Globe className="w-10 h-10" />,
      color: "bg-cyan-400",
    },
    {
      title: "ポイント機能",
      description: "ユーザーのリピート率向上に貢献します。",
      icon: <CheckCircle2 className="w-10 h-10" />,
      color: "bg-lime-400",
    },
    {
      title: "SMS認証機能",
      description: "安心・安全な本人確認で信頼性を確保。",
      icon: <Shield className="w-10 h-10" />,
      color: "bg-red-400",
    },
  ];

  const steps = [
    { number: "01", title: "アカウント作成", description: "無料で会員登録", color: "bg-yellow-400" },
    { number: "02", title: "サイト開設", description: "商品・オリパを設定", color: "bg-blue-400" },
    { number: "03", title: "決済加盟店審査", description: "古物商許可証などを提出", color: "bg-pink-400" },
    { number: "04", title: "公開開始", description: "審査通過後すぐに販売", color: "bg-green-400" },
  ];

  const developmentSteps = [
    {
      number: "01",
      title: "商品を登録",
      description: "カード名を検索して商品を追加。画像や情報は自動で取得されます。",
      icon: <Package className="w-8 h-8" />,
      color: "bg-blue-400"
    },
    {
      number: "02",
      title: "在庫を追加",
      description: "商品ごとに在庫数を設定。グレード（美品・傷ありなど）も管理できます。",
      icon: <Layers className="w-8 h-8" />,
      color: "bg-purple-400"
    },
    {
      number: "03",
      title: "オリパを作成",
      description: "オリパ名・価格・口数を設定し、在庫から商品を割り当てます。",
      icon: <Settings className="w-8 h-8" />,
      color: "bg-orange-400"
    },
    {
      number: "04",
      title: "公開する",
      description: "プレビューで確認後、ワンクリックで公開。すぐにユーザーが購入可能に。",
      icon: <Eye className="w-8 h-8" />,
      color: "bg-green-400"
    },
  ];

  // アニメーションバリアント
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [0, -15, 0],
      rotate: [0, 5, 0, -5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  // セクション用のスクロールアニメーション
  const stepsAnimation = useScrollAnimation();
  const developmentAnimation = useScrollAnimation();
  const featuresAnimation = useScrollAnimation();
  const pricingAnimation = useScrollAnimation();
  const statsAnimation = useScrollAnimation();

  return (
    <>
      <Helmet>
        <title>オリパサイト簡単構築プラットフォーム | JOYBASE</title>
        <meta name="description" content="JOYBASEはオリパサイトを簡単に開設できるSaaSサービスです。初期費用・月額費用0円でサイト開設を申し込めます。販売開始には決済加盟店審査が必要です。" />
        <meta property="og:title" content="オリパサイト簡単構築プラットフォーム | JOYBASE" />
        <meta property="og:description" content="JOYBASEはオリパサイトを簡単に開設できるSaaSサービスです。初期費用・月額費用0円でサイト開設を申し込めます。販売開始には決済加盟店審査が必要です。" />
        <meta name="keywords" content="オリパ 構築, オリパサイト 構築, オリパサイト 作り方, ネットオリパ 作り方, オリパ 開業, オリパサイト SaaS, オリパ プラットフォーム, トレーディングカード オリパ, ポケカ オリパ 作り方" />
        <link rel="canonical" href="https://www.joybase.jp/" />
        <meta property="og:url" content="https://www.joybase.jp/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="JOYBASE" />
        <meta property="og:image" content="https://www.joybase.jp/ogp.svg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="ja_JP" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="オリパサイト簡単構築プラットフォーム | JOYBASE" />
        <meta name="twitter:description" content="JOYBASEは初期費用・月額費用0円でオリパサイト開設を申し込めるSaaSサービスです。販売開始には決済加盟店審査が必要です。" />
        <meta name="twitter:image" content="https://www.joybase.jp/ogp.svg" />
        <meta name="robots" content="index, follow" />
        <html lang="ja" />
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "JOYBASE",
            "applicationCategory": "WebApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "JPY"
            },
            "description": "JOYBASE はオリパサイトを簡単に構築できるSaaSサービスです。ポケカ、遊戯王、ワンピースなどのトレーディングカードのオリパを始めるのに最適です。オリパ構築・オリパサイト作成がわからなくても簡単に始められます。",
            "url": "https://www.joybase.jp/"
          }
        `}
        </script>
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "JOYBASE",
            "url": "https://www.joybase.jp/",
            "logo": "https://www.joybase.jp/logo.svg",
            "description": "オリパサイト構築プラットフォーム"
          }
        `}
        </script>
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "オリパサイトを構築するのに初期費用はかかりますか？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "JOYBASEは初期費用0円、月額費用0円でオリパサイトを構築できます。実際にオリパが購入されたときのみ手数料が発生します。"
                }
              },
              {
                "@type": "Question",
                "name": "オリパサイトの構築に技術知識は必要ですか？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "いいえ、技術知識は不要です。JOYBASEは直感的な管理画面で、誰でも簡単にオリパサイトを構築・運営できます。"
                }
              },
              {
                "@type": "Question",
                "name": "オリパサイトを始めるのに必要なものは何ですか？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "古物商許可証と銀行口座を用意してサイト開設を申し込み、決済加盟店審査の完了後に販売を開始できます。"
                }
              }
            ]
          }
        `}
        </script>
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "JOYBASE",
            "url": "https://www.joybase.jp/",
            "description": "オリパサイト簡単構築プラットフォーム",
            "publisher": {
              "@type": "Organization",
              "name": "合同会社コーディングホリック",
              "url": "https://codingholic.jp/"
            }
          }
        `}
        </script>
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SiteNavigationElement",
                "name": "特徴",
                "url": "https://www.joybase.jp/features"
              },
              {
                "@type": "SiteNavigationElement",
                "name": "料金",
                "url": "https://www.joybase.jp/pricing"
              },
              {
                "@type": "SiteNavigationElement",
                "name": "ブログ",
                "url": "https://www.joybase.jp/blog"
              },
              {
                "@type": "SiteNavigationElement",
                "name": "よくある質問",
                "url": "https://www.joybase.jp/faq"
              },
              {
                "@type": "SiteNavigationElement",
                "name": "お問い合わせ",
                "url": "https://www.joybase.jp/contact"
              },
              {
                "@type": "SiteNavigationElement",
                "name": "オリパ始め方ガイド",
                "url": "https://www.joybase.jp/blog/how-to-start"
              }
            ]
          }
        `}
        </script>
      </Helmet>

      <main className="bg-[#FFFEF5] overflow-hidden">
        {/* HERO Section - Neobrutalism */}
        <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28">
          {/* 浮遊するデコレーション要素 */}
          <motion.div
            variants={floatingVariants}
            animate="animate"
            className="absolute top-32 left-[10%] w-16 h-16 bg-yellow-400 border-4 border-black rounded-lg shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hidden lg:block"
          />
          <motion.div
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: "1s" }}
            className="absolute top-48 right-[15%] w-12 h-12 bg-pink-400 border-4 border-black rounded-full shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hidden lg:block"
          />
          <motion.div
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: "2s" }}
            className="absolute bottom-32 left-[20%] w-10 h-10 bg-blue-400 border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hidden lg:block"
          />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
              {/* Left Content */}
              <motion.div
                className="flex-1 text-center lg:text-left"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-black mb-6 leading-tight tracking-tight">
                  オリパサイトを<br />
                  <span className="relative inline-block">
                    <span className="relative z-10">簡単に</span>
                    <span className="absolute bottom-2 left-0 w-full h-4 bg-yellow-400 -z-0"></span>
                  </span>
                  構築
                </h1>

                <p className="text-xl lg:text-2xl text-gray-800 mb-10 max-w-2xl font-medium">
                  古物商許可証と銀行口座があれば、<br />
                  <span className="font-black text-black">初期費用0円</span>でサイト開設を申し込めます
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <motion.a
                    href="https://dashboard.joybase.jp/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-8 py-5 bg-black text-white border-4 border-black rounded-xl font-bold text-lg shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    無料でサイト開設を申し込む
                    <ArrowRight className="w-5 h-5" />
                  </motion.a>
                  <motion.a
                    href="https://demo.joybase.jp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-8 py-5 bg-white text-black border-4 border-black rounded-xl font-bold text-lg shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Play className="w-5 h-5" />
                    DEMO版を見る
                  </motion.a>
                </div>

                <div className="mt-10 flex flex-wrap gap-6 justify-center lg:justify-start text-base font-bold text-gray-900">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-black" />
                    </div>
                    初期費用0円
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-black" />
                    </div>
                    月額費用0円
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-black" />
                    </div>
                    技術知識不要
                  </div>
                </div>
              </motion.div>

              {/* Right Image */}
              <motion.div
                className="flex-1 w-full max-w-2xl"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              >
                <div className="relative">
                  <div className="absolute -inset-4 bg-yellow-400 border-4 border-black rounded-2xl -z-10 transform rotate-2"></div>
                  <img
                    src="./hero-mockup.svg"
                    alt="JOYBASEでオリパサイトを開設 - 店舗ダッシュボードとガチャ画面"
                    className="w-full h-auto rounded-xl border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* サーバー費用込みバナー - 超目立つ */}
        <section className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 border-y-4 border-black py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white border-4 border-black rounded-xl flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                  <Shield className="w-6 h-6 text-black" />
                </div>
                <div>
                  <p className="text-2xl md:text-3xl font-black text-black">
                    ドメイン・サーバー費用込み
                  </p>
                </div>
              </div>
              <div className="bg-black text-white px-4 py-2 rounded-lg border-2 border-black">
                <p className="text-sm font-bold text-center">
                  ドメイン・サーバー費用はシステム利用料に含まれます
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section - Service Features */}
        <motion.section
          ref={statsAnimation.ref}
          className="py-12 bg-black"
          initial="hidden"
          animate={statsAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-8"
              variants={containerVariants}
            >
              {[
                { value: 0, suffix: "円", label: "初期費用・月額費用", isZero: true },
                { value: 14, suffix: "日〜", label: "最短開設期間" },
                { text: "メール", label: "サポート対応" },
                { value: 100, suffix: "%", label: "オリパ機能搭載" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  variants={itemVariants}
                >
                  <div className="text-4xl md:text-5xl font-black text-yellow-400 mb-2">
                    {stat.text ? (
                      <span>{stat.text}</span>
                    ) : stat.isZero ? (
                      <span>{stat.value}{stat.suffix}</span>
                    ) : (
                      <CountUp end={stat.value} suffix={stat.suffix} />
                    )}
                  </div>
                  <div className="text-white font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* ニュースセクション */}
        {joybaseNews.length > 0 && (
          <section className="py-10 bg-white border-b-4 border-black">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black text-black flex items-center gap-2">
                  <span className="w-3 h-8 bg-yellow-400 border-2 border-black"></span>
                  NEWS
                </h2>
                <Link
                  to="/news"
                  className="text-sm font-bold text-black hover:text-yellow-600 flex items-center gap-1 border-2 border-black px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
                >
                  すべて見る
                  <ChevronRight size={16} />
                </Link>
              </div>
              <div className="space-y-3">
                {joybaseNews.slice(0, 3).map((news, index) => (
                  <motion.div
                    key={news.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/news/${news.slug}`}
                      className="flex items-center gap-4 text-sm hover:bg-yellow-50 border-2 border-transparent hover:border-black rounded-lg px-4 py-3 transition-all"
                    >
                      <span className="text-xs text-gray-500 font-mono whitespace-nowrap">{news.date}</span>
                      <span className="text-xs px-3 py-1 bg-yellow-400 text-black font-bold rounded-full border-2 border-black whitespace-nowrap">
                        {news.category}
                      </span>
                      <span className="text-gray-900 font-medium truncate">{news.title}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 利用開始までの流れ */}
        <motion.section
          ref={stepsAnimation.ref}
          className="py-24 bg-[#FFFEF5]"
          initial="hidden"
          animate={stepsAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-16" variants={itemVariants}>
              <h2 className="text-4xl lg:text-5xl font-black text-black mb-4">
                利用開始までの流れ
              </h2>
              <p className="text-xl text-gray-700 font-medium">
                たった4ステップで、あなたのオリパサイトが完成します
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  variants={itemVariants}
                >
                  <div className={`${step.color} border-4 border-black rounded-2xl p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all h-full`}>
                    <div className="text-5xl font-black text-black/20 mb-4">{step.number}</div>
                    <h3 className="text-xl font-black mb-2 text-black">{step.title}</h3>
                    <p className="text-gray-800 font-medium">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-black" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div className="text-center mt-12" variants={itemVariants}>
              <Link
                to="/how-to-start"
                className="inline-flex items-center gap-2 text-black font-bold text-lg border-b-4 border-black hover:border-yellow-400 transition-colors"
              >
                詳しい始め方を見る
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </motion.section>

        {/* オリパ開発から公開までの流れ */}
        <motion.section
          ref={developmentAnimation.ref}
          className="py-24 bg-white border-t-4 border-black"
          initial="hidden"
          animate={developmentAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-16" variants={itemVariants}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 border-4 border-black rounded-full text-sm font-black mb-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                <Settings className="w-4 h-4" />
                サイト開設後の作業
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-black mb-4">
                オリパ開発から公開までの流れ
              </h2>
              <p className="text-xl text-gray-700 font-medium">
                管理画面から簡単にオリパを作成・公開できます
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {developmentSteps.map((step, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  variants={itemVariants}
                >
                  <div className={`bg-white border-4 border-black rounded-2xl p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all h-full`}>
                    <div className={`w-14 h-14 ${step.color} border-4 border-black rounded-xl flex items-center justify-center mb-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]`}>
                      {step.icon}
                    </div>
                    <div className="text-sm font-black text-gray-400 mb-1">STEP {step.number}</div>
                    <h3 className="text-xl font-black mb-2 text-black">{step.title}</h3>
                    <p className="text-gray-700 font-medium leading-relaxed">{step.description}</p>
                  </div>
                  {index < developmentSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-black" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div className="mt-12 bg-yellow-100 border-4 border-black rounded-2xl p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" variants={itemVariants}>
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 bg-yellow-400 border-4 border-black rounded-xl flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex-shrink-0">
                  <Sparkles className="w-8 h-8 text-black" />
                </div>
                <div className="text-center md:text-left">
                  <h4 className="text-xl font-black text-black mb-2">ポケモンカードなら画像自動取得！</h4>
                  <p className="text-gray-700 font-medium">
                    カード名を検索するだけで、カード画像・レアリティ・セット情報などが自動で取得されます。<br className="hidden md:block" />
                    遊戯王・ワンピース・ヴァンガードにも対応予定です。
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* デモ店舗バナー */}
        <section className="py-12 bg-[#FFFEF5]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.a
              href="https://demo.joybase.jp"
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <div className="relative bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 border-4 border-black rounded-2xl p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] group-hover:translate-x-1 group-hover:translate-y-1 transition-all overflow-hidden">
                {/* デコレーション */}
                <div className="absolute top-4 right-4 w-20 h-20 bg-white/30 rounded-full border-4 border-black/20"></div>
                <div className="absolute bottom-4 left-8 w-12 h-12 bg-white/30 rounded-lg border-4 border-black/20 rotate-12"></div>

                <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="text-center md:text-left">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-full text-sm font-bold mb-4">
                      <Play className="w-4 h-4" />
                      デモストア公開中
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-black mb-2">
                      実際のオリパサイトを体験してみよう
                    </h3>
                    <p className="text-black/80 font-medium">
                      JOYBASEで構築されたデモ店舗で、ユーザー目線のオリパ体験ができます
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white border-4 border-black rounded-xl font-bold text-lg shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)] group-hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)] transition-all">
                      デモストアを見る
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </div>
            </motion.a>
          </div>
        </section>

        {/* 特徴 Section */}
        <motion.section
          ref={featuresAnimation.ref}
          id="features"
          className="py-24 bg-white border-t-4 border-b-4 border-black"
          initial="hidden"
          animate={featuresAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-16" variants={itemVariants}>
              <h2 className="text-4xl lg:text-5xl font-black text-black mb-4">
                JOYBASEの特徴
              </h2>
              <p className="text-xl text-gray-700 font-medium">
                オリパサイト運営に必要な機能がすべて揃っています
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={containerVariants}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className={`${feature.color} border-4 border-black rounded-2xl p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all cursor-default`}
                  variants={itemVariants}
                  whileHover={{ rotate: [-1, 1, 0] }}
                >
                  <div className="w-16 h-16 bg-white border-4 border-black rounded-xl flex items-center justify-center mb-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-black mb-2 text-black">{feature.title}</h3>
                  <p className="text-gray-800 font-medium leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div className="text-center mt-12" variants={itemVariants}>
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-black font-bold text-lg border-b-4 border-black hover:border-yellow-400 transition-colors"
              >
                すべての機能を詳しく見る
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </motion.section>

        {/* 料金 Section */}
        <motion.section
          ref={pricingAnimation.ref}
          id="pricing"
          className="py-24 bg-[#FFFEF5]"
          initial="hidden"
          animate={pricingAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-16" variants={itemVariants}>
              <h2 className="text-4xl lg:text-5xl font-black text-black mb-4">
                シンプルな料金体系
              </h2>
              <p className="text-xl text-gray-700 font-medium">
                まずは無料で始めましょう！
              </p>
            </motion.div>

            <motion.div
              className="bg-white border-4 border-black rounded-3xl overflow-hidden shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              variants={itemVariants}
            >
              <div className="p-8 bg-yellow-400 border-b-4 border-black">
                <h3 className="text-3xl font-black text-black">スタンダードプラン</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 divide-y-4 md:divide-y-0 md:divide-x-4 divide-black">
                <div className="p-8">
                  <h4 className="text-xl font-black mb-6 text-black flex items-center gap-2">
                    <span className="w-2 h-6 bg-green-400 border-2 border-black"></span>
                    オリパサイトを始めるとき
                  </h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-green-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">初期費用</span>
                      <span className="text-4xl font-black text-black">0円</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-green-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">月額費用</span>
                      <span className="text-4xl font-black text-black">0円</span>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <h4 className="text-xl font-black mb-6 text-black flex items-center gap-2">
                    <span className="w-2 h-6 bg-yellow-400 border-2 border-black"></span>
                    ポイント購入したとき
                  </h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-yellow-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">カード・ウォレット決済手数料</span>
                      <span className="text-4xl font-black text-yellow-600">3.6%</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-yellow-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">システム利用料</span>
                      <span className="text-4xl font-black text-yellow-600">3%</span>
                    </div>
                    <p className="text-sm text-gray-600 font-medium">
                      ※ 表示の手数料率はすべて税抜です。別途消費税がかかります。
                    </p>
                    <p className="text-sm text-gray-600 font-medium">
                      ※ PayPayの場合は決済手数料9.0%（準備中）です。
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-100 border-t-4 border-black space-y-3">
                <p className="text-base text-gray-800 font-bold flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-black" />
                  </div>
                  実際にユーザーがオリパを購入するまで料金は発生しません
                </p>
                <p className="text-base text-gray-800 font-bold flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-black" />
                  </div>
                  ドメイン・サーバー費用はシステム利用料に含まれています
                </p>
              </div>

              {/* 追加の強調ポイント - サーバー費用 */}
              <div className="p-6 bg-gradient-to-r from-green-100 to-emerald-100 border-t-4 border-green-500">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-green-400 border-4 border-black rounded-xl flex items-center justify-center flex-shrink-0 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    <Shield className="w-7 h-7 text-black" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <p className="text-xl font-black text-black">ドメイン・サーバー費用込み</p>
                      <span className="px-3 py-1 bg-green-400 border-2 border-black text-xs font-black">サーバー費用込み</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      ドメイン・サーバー費用は、アクセス規模にかかわらずシステム利用料に含まれます。<br />
                      <span className="font-bold">イベント時のアクセス集中や会員数の増加にも、追加のサーバー費用なしで対応します。</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div className="text-center mt-12" variants={itemVariants}>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-black font-bold text-lg border-b-4 border-black hover:border-yellow-400 transition-colors"
              >
                料金について詳しく見る
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <section className="py-24 bg-black relative overflow-hidden">
          {/* デコレーション */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-10 left-10 w-32 h-32 border-4 border-yellow-400/30 rounded-xl"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-10 right-10 w-40 h-40 border-4 border-pink-400/30 rounded-full"
          />

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
                <span className="text-yellow-400">オリパサイト</span>開設を申し込もう
              </h2>
              <p className="text-xl text-gray-300 mb-10 font-medium">
                初期費用・月額費用0円でサイト開設を申し込めます
              </p>

              <motion.a
                href="https://dashboard.joybase.jp/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-12 py-6 bg-yellow-400 text-black border-4 border-yellow-400 rounded-xl font-black text-xl shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                無料でサイト開設を申し込む
                <ArrowRight className="w-6 h-6" />
              </motion.a>

              <div className="mt-10 flex flex-wrap gap-6 justify-center text-white font-bold">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 hover:text-yellow-400 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  担当者にお問い合わせ
                </a>
                <Link
                  to="/how-to-start"
                  className="inline-flex items-center gap-2 hover:text-yellow-400 transition-colors"
                >
                  始め方ガイドを見る
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/blog/how-to-create-oripa-site"
                  className="inline-flex items-center gap-2 hover:text-yellow-400 transition-colors"
                >
                  オリパサイト構築方法を比較
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
};

export default IndexPage;
